import type { LeadFilter, LeadListQuery, TriState } from '../../api/types';

/**
 * The folder Leads tab's filters as they sit in the URL (so a filtered view
 * can be linked), and how they map to the contract's leads query.
 */
export const LEAD_URL_DEFAULTS = {
  presenter: '',
  q: '',
  state: '',
  type: '',
  foundVia: '',
  assignee: '',
  minF: '',
  maxF: '',
  minEng: '',
  maxEng: '',
  email: 'any',
  done: 'any',
  sort: '-added',
  page: 1,
};

export type LeadUrlState = typeof LEAD_URL_DEFAULTS;

export const LEADS_PAGE_SIZE = 50;

function num(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function tri(raw: string): TriState | undefined {
  return raw === 'yes' || raw === 'no' ? raw : undefined;
}

/** The filter part only (no paging/sort) — what bulk actions send with `all: true`. */
export function toLeadFilter(s: LeadUrlState): LeadFilter {
  const f: LeadFilter = {};
  if (['face', 'faceless', 'unchecked', 'unknown'].includes(s.presenter)) f.presenter = s.presenter as LeadFilter['presenter'];
  if (s.q.trim()) f.q = s.q.trim();
  if (s.state) f.state = s.state;
  if (s.type) f.type = s.type;
  if (s.foundVia) f.foundVia = s.foundVia;
  if (s.assignee) f.assignee = s.assignee;
  const minF = num(s.minF);
  const maxF = num(s.maxF);
  const minE = num(s.minEng);
  const maxE = num(s.maxEng);
  if (minF !== undefined) f.minFollowers = minF;
  if (maxF !== undefined) f.maxFollowers = maxF;
  if (minE !== undefined) f.minEngagement = minE;
  if (maxE !== undefined) f.maxEngagement = maxE;
  const email = tri(s.email);
  const done = tri(s.done);
  if (email) f.email = email;
  if (done) f.done = done;
  return f;
}

export function toLeadQuery(s: LeadUrlState, limit = LEADS_PAGE_SIZE): LeadListQuery {
  return { ...toLeadFilter(s), page: Math.max(1, s.page), limit, ...(s.sort ? { sort: s.sort } : {}) };
}

/** How many filters differ from the defaults (for the "Clear" affordance). */
export function activeFilterCount(s: LeadUrlState): number {
  return Object.keys(toLeadFilter(s)).length;
}

/** Filters reset; sort is a view preference and survives. */
export function clearedFilters(s: LeadUrlState): Partial<LeadUrlState> {
  const { sort } = s;
  return { ...LEAD_URL_DEFAULTS, sort };
}
